import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { SlideOver, Field, inputCls, Btn } from "../ui";
import { usePartner, APPOINTMENT_TYPE_LABELS, APPOINTMENT_LOCATION_LABELS } from "../PartnerContext";

const emptyForm = (prospectId = "") => ({
  prospect_id: prospectId, date: dayjs().format("YYYY-MM-DD"), start_time: "14:00", end_time: "15:00",
  appointment_type: "PREMIER_RDV", location_type: "AGENCE", location_address: "", notes: "",
});

export const AppointmentModal = ({ open, onClose, prospectId, projectRef, editing }) => {
  const { territorialProspects, createAppointment, updateAppointment } = usePartner();
  const [form, setForm] = useState(emptyForm(prospectId || ""));
  const [query, setQuery] = useState("");
  const locked = !!prospectId;
  const list = territorialProspects || [];

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        prospect_id: editing.prospect_id,
        date: dayjs(editing.start_at).format("YYYY-MM-DD"),
        start_time: dayjs(editing.start_at).format("HH:mm"),
        end_time: dayjs(editing.end_at).format("HH:mm"),
        appointment_type: editing.appointment_type, location_type: editing.location_type,
        location_address: editing.location_address || "", notes: editing.notes || "",
      });
    } else {
      setForm(emptyForm(prospectId || ""));
    }
    setQuery("");
  }, [open, editing, prospectId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return list.slice(0, 8);
    const q = query.toLowerCase();
    return list.filter((p) => `${p.name} ${p.city}`.toLowerCase().includes(q)).slice(0, 8);
  }, [list, query]);
  const selected = list.find((p) => p.id === form.prospect_id);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickProspect = (p) => {
    setForm((f) => ({ ...f, prospect_id: p.id }));
    setQuery("");
  };

  const onLocationTypeChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f, location_type: v,
      location_address: v === "CHEZ_CLIENT" && !f.location_address && selected ? (selected.address || selected.city || "") : f.location_address,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prospect_id) {
      toast.error("Sélectionnez un prospect ou un client.");
      return;
    }
    const payload = {
      prospect_id: form.prospect_id,
      ...(projectRef && !editing ? { project_ref: projectRef } : {}),
      start_at: `${form.date}T${form.start_time}:00`,
      end_at: `${form.date}T${form.end_time}:00`,
      appointment_type: form.appointment_type, location_type: form.location_type,
      location_address: form.location_address, notes: form.notes,
    };
    const ok = editing ? await updateAppointment(editing.id, payload) : await createAppointment(payload);
    if (ok) onClose();
  };

  return (
    <SlideOver open={open} onClose={onClose} testid="appointment-modal" eyebrow={editing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"} title="Rendez-vous">
      <form onSubmit={submit} className="space-y-4" data-testid="appointment-form">
        {!locked && (
          <Field label="Prospect / Client *">
            {selected ? (
              <div className="flex items-center justify-between rounded-xl border border-[#D9E0E2] bg-petrol-bg/40 px-3.5 py-2.5 text-sm">
                <span className="font-semibold text-navy" data-testid="appointment-prospect-selected">
                  {selected.name} — {selected.phase3ProjectId ? "Client" : "Prospect"}
                </span>
                <button type="button" data-testid="appointment-prospect-clear" onClick={() => setForm((f) => ({ ...f, prospect_id: "" }))}>
                  <X className="h-3.5 w-3.5 text-stone-400" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                  <input
                    data-testid="appointment-prospect-search"
                    className={`${inputCls} pl-9`}
                    placeholder="Rechercher un prospect ou client…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                {filtered.length > 0 && (
                  <ul data-testid="appointment-prospect-options" className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-xl border border-[#EDF0F1] p-1">
                    {filtered.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          data-testid={`appointment-prospect-option-${p.id}`}
                          onClick={() => pickProspect(p)}
                          className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-petrol-bg"
                        >
                          {p.name} <span className="text-xs text-stone-400">— {p.phase3ProjectId ? "Client" : "Prospect"} · {p.city}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </Field>
        )}
        {locked && selected && (
          <div className="rounded-xl bg-petrol-bg/40 px-3.5 py-2.5 text-sm font-semibold text-navy" data-testid="appointment-prospect-locked">
            {selected.name}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Date *"><input type="date" data-testid="appointment-date" className={inputCls} value={form.date} onChange={set("date")} /></Field>
          <Field label="Début *"><input type="time" data-testid="appointment-start-time" className={inputCls} value={form.start_time} onChange={set("start_time")} /></Field>
          <Field label="Fin *"><input type="time" data-testid="appointment-end-time" className={inputCls} value={form.end_time} onChange={set("end_time")} /></Field>
        </div>
        <Field label="Type de rendez-vous">
          <select data-testid="appointment-type" className={inputCls} value={form.appointment_type} onChange={set("appointment_type")}>
            {Object.entries(APPOINTMENT_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lieu">
            <select data-testid="appointment-location-type" className={inputCls} value={form.location_type} onChange={onLocationTypeChange}>
              {Object.entries(APPOINTMENT_LOCATION_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Adresse / précision"><input data-testid="appointment-location-address" className={inputCls} value={form.location_address} onChange={set("location_address")} /></Field>
        </div>
        <Field label="Notes"><textarea rows={3} data-testid="appointment-notes" className={inputCls} value={form.notes} onChange={set("notes")} /></Field>
        <Btn type="submit" testid="appointment-submit" className="w-full">{editing ? "Enregistrer les modifications" : "Créer le rendez-vous"}</Btn>
      </form>
    </SlideOver>
  );
};
